import _ from 'lodash'
import React, {useEffect, useState} from 'react'
import {Checkbox, Dropdown, Icon, Menu, Popup} from 'semantic-ui-react'
import {useGesture} from '@use-gesture/react'
import {BsLock, BsUnlock} from 'react-icons/bs'
import {labelingWidgetMap} from '@/features/labeling'
import {IEvent, ILabel, IUserLabelsMap} from '@/features/labeling'
import {getDuration, IPlotData} from '@/utils/media'
import {hslToHex} from '@/utils/colors'
import {Plot} from '@/features/plots'
import {secondsToTimeString, useConfig} from '../..'
import {IDataSourceItem, IDataSourceStats} from '@/features/data-sources'
import './style.css'

export interface ITimelineRow {
  key: string
  groups: string[]
  title?: string
  events?: IEvent[]
  labels?: ILabel<object>[]
}

export interface ITimelineProps {
  predictionStats?: IDataSourceStats
  data?: Pick<IDataSourceItem, 'predictions' | 'plots'>
  duration?: number
  actionsLeft?: React.ReactNode
  actionsRight?: React.ReactNode
  currentValue?: number
  userLabels?: IUserLabelsMap
  currentEvent?: IEvent
  enableClicks?: boolean
  enableBackgroundClicks?: boolean
  showActions?: boolean
  showTimecode?: boolean
  showLabels?: boolean
  fluid?: boolean
  plotHeight?: number
  userCanAddRows?: boolean
  userChannels?: string[]
  channelLock?: string[]
  channelTypeDescriptions?: {
    [key: string]: string
  }
  setUserChannels?: React.Dispatch<React.SetStateAction<string[] | undefined>>
  toggleChannelLock?: (key: string) => void
  onValueClick?: (value: IEvent) => void
  onTimeDoubleClick?: () => void
  findPredictionFromLabel?: (label: ILabel<object>) => IEvent | undefined
  // findLabelFromPrediction?: (event:IEvent) => ILabel<object> | undefined,
}

export function Timeline(props: ITimelineProps) {
  const {
    data,
    duration,
    actionsLeft,
    actionsRight,
    currentValue,
    userLabels,
    currentEvent,
    enableClicks,
    enableBackgroundClicks,
    showActions,
    showTimecode,
    showLabels,
    fluid,
    plotHeight,
    userCanAddRows,
    userChannels,
    channelLock,
    channelTypeDescriptions,
    setUserChannels,
    toggleChannelLock,
    onValueClick,
    onTimeDoubleClick,
    findPredictionFromLabel,
  } = {
    enableClicks: true,
    enableBackgroundClicks: true,
    showTimecode: true,
    showActions: true,
    showLabels: true,
    ...props,
  }

  const {denoise, denoiseThreshold, formatTime, updateConfig} = useConfig()

  const staticKnownTrackTypes = ['intro', 'recap', 'credit', 'preview', 'adbreak']
  const [knownTrackTypes, setKnownTrackTypes] = useState(staticKnownTrackTypes)
  const [knownTrackTypesNotSeen, setKnownTrackTypesNotSeen] = useState<string[]>()
  const [isUserTrackPopupOpen, setIsUserTrackPopupOpen] = useState(false)
  const [cursorPercent, setCursorPercent] = useState<number | undefined>()
  const [cursorTime, setCursorTime] = useState<string | undefined>()
  const [invertTime, setInvertTime] = useState(false)
  const [rows, setRows] = useState<ITimelineRow[]>()
  const [selectedEvent, setSelectedEvent] = useState<IEvent>()
  const [maxValue, setMaxValue] = useState<number>()
  const [maxValueTime, setMaxValueTime] = useState<string>()
  const [plots, setPlots] = useState<{[name: string]: IPlotData}>()
  const [plotRanges, setPlotRanges] = useState<{
    x: [number, number]
    y: [number, number]
  }>({
    x: [0, 1],
    y: [0, 1],
  })

  const bindGestures = useGesture({
    onMouseMove: (e) => {
      const x = mousePosToPercent(e.event)
      if (x === undefined) return
      setCursorPercent(x)
    },
    onMouseOut: () => {
      setCursorPercent(undefined)
    },
    onDrag: (e) => {
      if (!enableClicks) return
      if (!enableBackgroundClicks) return
      if (!maxValue) return
      const elem = e.event.target as HTMLDivElement
      if (!elem.classList.contains('values')) return
      const [type] = e.args
      const bounds = elem.getBoundingClientRect()
      const x = _.sortBy([
        _.clamp((e.initial[0] - bounds.x) / bounds.width, 0, 1) * maxValue,
        _.clamp((e.values[0] - bounds.x) / bounds.width, 0, 1) * maxValue,
      ])
      const res: IEvent = {
        type,
        start: x[0],
      } as IEvent
      if (x[1] - x[0] > 1) {
        res.end = x[1]
      }
      setSelectedEvent(res)
    },
    onDragEnd: () => {
      if (!enableBackgroundClicks) return
      if (!selectedEvent) return
      handleOnPredictionClick(selectedEvent)
    },
    // onClick: (e) => {
    //     if (!enableClicks) return;
    //     const [type] = e.args;
    //     if (maxValue === undefined) return;
    //     const x = mousePosToPercent(e.event);
    //     if (x === undefined) return;
    //     handleOnPredictionClick({
    //         type,
    //         start: x * maxValue,
    //     });
    // },
  })

  const bindValueGestures = useGesture({
    onMouseMove: (e) => {
      if (!maxValue) return
      setCursorPercent(e.args[0].start / maxValue)
    },
  })

  useEffect(() => {
    if (currentEvent === selectedEvent) return
    setSelectedEvent(currentEvent)
  }, [currentEvent])

  useEffect(() => {
    const _maxValExtra = 5
    let res = _.chain(data)
      .get('predictions')
      .groupBy((v) => v.type)
      .toPairs()
      .sortBy(([k]) => k)
      .concat(_.map(userChannels, (t) => [t, []] as [string, IEvent[]]))
      .uniqBy(([k]) => k)
      .map(([key, events]) => {
        const groups = _.chain(events)
          .map((e) => e.group)
          .compact()
          .uniq()
          .sort()
          .value()
        const res: ITimelineRow = {
          key,
          events,
          groups,
        }
        if (_.has(userLabels, key)) {
          res.labels = _.chain(userLabels).get(key).values().value()
        }
        return res
      })
      .value()
    if (denoise) {
      const valueFilter = (values: IEvent[]) => {
        let prev: number | undefined
        return _.filter(values, (v) => {
          const _keep = prev === undefined || v.start - prev > denoiseThreshold
          prev = v.start
          return _keep
        })
      }
      res = _.chain(res)
        .map((row) => {
          if (!row.events) return row
          return {
            ...row,
            events: valueFilter(row.events),
          }
        })
        .value()
    }
    setRows(res)
    let resMax = _.chain(res)
      .map((row) => row.events || [])
      .flatten()
      .map((v) => {
        if (v.start === undefined) return []
        return _.filter([v.start + _maxValExtra, v.end], (vv) => vv !== undefined)
      })
      .flatten()
      .concat([getDuration(data as Partial<IDataSourceItem>) || 0, duration || 0])
      .compact()
      .max()
      .value()
    if (userLabels) {
      const _labelsMaxValue = _.chain(userLabels)
        .values()
        .map(_.values)
        .flatten()
        .map((v) => {
          if (v.start === undefined) return []
          return _.filter([v.start + _maxValExtra, v.end], (vv) => vv !== undefined)
        })
        .flatten()
        .max()
        .value()
      resMax = _.max([resMax, _labelsMaxValue])
    }
    if (data?.plots) {
      const plotRanges = _.chain(data)
        .get('plots')
        .toPairs()
        .map(([_k, {data}]) => {
          return _.map(data, (vv) => {
            // let xMin = Infinity;
            const xMin = 0 // NOTE: plots need to always start at zero in timeline
            let xMax = -Infinity
            let yMin = Infinity
            let yMax = -Infinity
            _.forEach(_.get(vv, 'x'), (x) => {
              if (!_.isNumber(x)) return
              // xMin = _.min([xMin, x]);
              xMax = _.max([xMax, x as number, resMax]) || 1
            })
            _.forEach(_.get(vv, 'y'), (y) => {
              yMin = _.min([yMin, y as number]) || 0
              yMax = _.max([yMax, y as number]) || 1
            })
            return {
              x: [xMin, xMax] as [number, number],
              y: [yMin, yMax] as [number, number],
            }
          })
        })
        .flatten()
        .reduce((prev, curr) => {
          return {
            x: [_.min([prev.x[0], curr.x[0]]) || 0, _.max([prev.x[1], curr.x[1]]) || 1],
            y: [_.min([prev.y[0], curr.y[0]]) || 0, _.max([prev.y[1], curr.y[1]]) || 1],
          }
        })
        .value()
      setPlotRanges(plotRanges)
      resMax = _.max([resMax, plotRanges.x[1]]) || 1
    }

    setPlots(_.get(data, 'plots'))
    setMaxValue(resMax)
  }, [data, denoise, denoiseThreshold, userLabels, duration, userChannels])

  useEffect(() => {
    const s = maxValue || 0
    setMaxValueTime(formatTime ? secondsToTimeString(s) : s.toFixed(2))
  }, [maxValue, formatTime])

  useEffect(() => {
    if (maxValue === undefined || maxValue === 0) return
    const s = (cursorPercent || 0) * (maxValue || 0)
    const t = currentValue || 0
    const tString =
      cursorPercent === undefined ?
        _.chain([
          invertTime ? '-' : undefined,
          formatTime ?
            secondsToTimeString(invertTime && duration ? duration - t : t)
          : t.toFixed(2),
        ])
          .compact()
          .join('')
          .value()
      : formatTime ? secondsToTimeString(s)
      : s.toFixed(2)
    setCursorTime(tString)
  }, [cursorPercent, currentValue, formatTime, invertTime, duration])

  useEffect(() => {
    setKnownTrackTypes(
      _.chain(staticKnownTrackTypes).union(_.keys(labelingWidgetMap)).without('default').value()
    )
  }, [labelingWidgetMap])

  useEffect(() => {
    const missing = _.chain(knownTrackTypes)
      .without(..._.map(rows, (r) => r.key))
      .value()
    setKnownTrackTypesNotSeen(missing)
  }, [knownTrackTypes, rows])

  function mousePosToPercent(e: MouseEvent) {
    const elem = e.target as HTMLDivElement
    if (!_.includes(elem.classList, 'values')) return
    const bounds = elem.getBoundingClientRect()
    return _.clamp((e.clientX - bounds.x) / bounds.width, 0, 1)
  }

  function computeStyle(v: IEvent): React.CSSProperties | undefined {
    if (v === undefined) return
    if (!maxValue) return
    const {start, end, hue} = v
    const res: React.CSSProperties = {
      left: `${(start / maxValue) * 100}%`,
    }
    if (end) {
      const w = ((end - start) / maxValue) * 100
      if (w) res.width = `${w}%`
    }
    if (_.isNumber(hue)) res.backgroundColor = hslToHex(hue)
    return res
  }

  function computeValueStyle(
    _key: string,
    v: IEvent,
    groups: string[]
  ): React.CSSProperties | undefined {
    const style = computeStyle(v)
    if (!style) return
    if (v.group) {
      const i = _.indexOf(groups, v.group)
      const itemOffset = (40 * groups.length - 40 * 0.2) / groups.length
      if (i > 0) style.top = `${itemOffset * i}px`
    }
    return style
  }

  function computeLabelStyle(key: string, v: ILabel<object>): React.CSSProperties | undefined {
    let style = computeStyle(v)
    if (style?.backgroundColor) delete style.backgroundColor
    const labelingWidgetConfig =
      labelingWidgetMap ? labelingWidgetMap[key] || labelingWidgetMap.default : undefined
    if (
      v.data !== undefined &&
      labelingWidgetConfig &&
      labelingWidgetConfig.computeLabelStyle !== undefined
    ) {
      style = {
        ...style,
        ...labelingWidgetConfig.computeLabelStyle(v.data),
      }
    }
    return style
  }

  function computeSelectionStyle(v: IEvent): React.CSSProperties | undefined {
    const style = computeStyle(v)
    if (!style) return
    if (style.backgroundColor) delete style.backgroundColor
    return style
  }

  function handleOnPredictionClick(value: IEvent) {
    if (!enableClicks) return
    if (!onValueClick) return
    onValueClick(value)
    return false
  }

  function handleOnLabelClick(value: ILabel<object>) {
    if (!enableClicks) return
    if (!onValueClick) return
    if (!findPredictionFromLabel) return onValueClick(value)
    onValueClick(findPredictionFromLabel(value) || value)
  }

  function currentCursorToPlotMarker(): number | undefined {
    if (!maxValue) return
    if (cursorPercent) return cursorPercent * maxValue
    if (currentValue) return currentValue
  }

  function toggleInvertTime() {
    setInvertTime((prev) => !prev)
  }

  function handleOnTimeDoubleClick() {
    if (onTimeDoubleClick) onTimeDoubleClick()
  }

  if (!rows) return null

  const classNames = _.chain([
    'timeline',
    !enableClicks ? 'noclick' : undefined,
    !showLabels ? 'nolabels' : undefined,
    fluid ? 'fluid' : undefined,
  ])
    .compact()
    .join(' ')
    .value()

  const showHeader = showActions || showTimecode

  return (
    <div className={classNames}>
      {showHeader ?
        <div className='actions'>
          {showActions ?
            <>
              {actionsLeft ?
                <div className='left'>{actionsLeft}</div>
              : null}
              <div className='right'>
                {actionsRight ? actionsRight : null}
                <div>
                  <Checkbox
                    label='denoise'
                    checked={denoise}
                    onChange={(_e, {checked}) => updateConfig({denoise: checked || false})}
                  />
                  <Dropdown
                    selection
                    compact
                    disabled={!denoise}
                    value={denoiseThreshold}
                    options={_.chain(_.range(1, 13))
                      .map((i) => ({text: i * 5, value: i * 5, key: i * 5}))
                      .concat(_.map([0.25, 0.5, 1], (i) => ({text: i, value: i, key: i})))
                      .sortBy((v) => v.value)
                      .value()}
                    onChange={(_e, {value}) => {
                      if (_.isNumber(value)) updateConfig({denoiseThreshold: value})
                    }}
                  />
                </div>
              </div>
            </>
          : null}
          {cursorTime && maxValueTime ?
            <div className='time'>
              <span
                onClick={toggleInvertTime}
                onDoubleClick={handleOnTimeDoubleClick}
              >
                {cursorTime}
              </span>{' '}
              / <span className='duration'>{maxValueTime}</span>
            </div>
          : null}
        </div>
      : null}
      <div className='channels'>
        {_.chain(rows)
          .map((row, i) => {
            const {key, title, groups, events, labels} = row
            const classNamesLabel = _.chain([
              'predictions',
              'label',
              currentEvent && currentEvent.type === key ? 'active' : undefined,
            ])
              .compact()
              .join(' ')
              .value()
            const classNamesValues = _.chain([
              'predictions',
              'values',
              currentEvent && currentEvent.type === key ? 'active' : undefined,
            ])
              .compact()
              .join(' ')
              .value()
            let cursorPos: number | undefined
            if (cursorPercent) cursorPos = cursorPercent
            else if (currentValue && maxValue) cursorPos = currentValue / maxValue
            const description = _.get(channelTypeDescriptions, _.toLower(key))
            const trackHeight = 40
            return [
              <div
                key={`label-${i}`}
                className={classNamesLabel}
                onClick={() => {
                  if (!currentEvent) return
                  handleOnPredictionClick({
                    type: key,
                    start: currentEvent.start,
                    end: currentEvent.end,
                  })
                }}
              >
                {description ?
                  <Popup
                    size='tiny'
                    trigger={<div>{title || key}</div>}
                    content={description}
                  />
                : title || key}
                {toggleChannelLock ?
                  <Popup
                    content={`lock labeling to "${key}"`}
                    size='tiny'
                    trigger={
                      <div
                        className={_.includes(channelLock, key) ? 'lock active' : 'lock'}
                        onClick={() => toggleChannelLock(key)}
                      >
                        {_.includes(channelLock, key) ?
                          <BsLock />
                        : <BsUnlock />}
                      </div>
                    }
                  />
                : null}
              </div>,
              <div
                key={`values-${i}`}
                className={classNamesValues}
                style={
                  groups?.length > 0 ?
                    {
                      height: `${trackHeight * groups.length}px`,
                    }
                  : undefined
                }
                {...bindGestures(key)}
              >
                {_.map(events, (v, vi) => {
                  if (v.hidden) return null
                  if (v.start === -1 || v.end === -1) return null
                  if (v.start === undefined) return null
                  const res = (
                    <div
                      key={`value-${i}-${vi}`}
                      className='value'
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        handleOnPredictionClick(v)
                        return false
                      }}
                      style={computeValueStyle(key, v, groups)}
                      {...bindValueGestures(v)}
                    />
                  )
                  if (v.text)
                    return (
                      <Popup
                        basic
                        key={res.key}
                        size='tiny'
                        trigger={res}
                        content={v.text}
                        style={{
                          whiteSpace: 'pre',
                        }}
                      />
                    )
                  return res
                })}
                {groups.length > 0 ?
                  <div className='groups'>
                    {_.map(groups, (g, gi) => {
                      if (!g) return
                      return (
                        <div
                          key={gi}
                          className='group'
                        >
                          <div>{g}</div>
                          <div>{g}</div>
                        </div>
                      )
                    })}
                  </div>
                : null}
                {cursorPos ?
                  <div
                    className='cursor'
                    style={{
                      left: `${cursorPos * 100}%`,
                    }}
                  />
                : null}
                {showLabels ?
                  <div className='labels'>
                    {_.map(labels, (v, vi) => {
                      if (v.start === -1 || v.end === -1) return null
                      return (
                        <div
                          key={`label-${i}-${vi}`}
                          className='label'
                          onClick={() => {
                            return handleOnLabelClick(v)
                          }}
                          style={computeLabelStyle(key, v)}
                          {...bindValueGestures(v)}
                        />
                      )
                    })}
                    {(
                      selectedEvent &&
                      selectedEvent.type === key &&
                      selectedEvent.start !== -1 &&
                      selectedEvent.end !== -1
                    ) ?
                      <div
                        className='selection anim pulse'
                        style={computeSelectionStyle(selectedEvent)}
                      />
                    : null}
                  </div>
                : null}
              </div>,
            ]
          })
          .value()}

        {userCanAddRows && knownTrackTypesNotSeen && knownTrackTypesNotSeen.length > 0 ?
          <Popup
            open={isUserTrackPopupOpen}
            size='tiny'
            closeOnTriggerMouseLeave={false}
            openOnTriggerMouseEnter={false}
            onOpen={() => setIsUserTrackPopupOpen(true)}
            onClose={() => setIsUserTrackPopupOpen(false)}
            trigger={
              <button
                children={<Icon name='add' />}
                style={{
                  background: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                }}
              />
            }
            content={
              <Menu
                vertical
                compact
                className='add-channel-menu'
              >
                {_.map(knownTrackTypesNotSeen, (t) => (
                  <Menu.Item
                    key={t}
                    icon='add'
                    content={t}
                    onClick={() => {
                      if (!setUserChannels) return
                      setUserChannels((prev) => [...(prev || []), t])
                      setIsUserTrackPopupOpen(false)
                    }}
                  />
                ))}
              </Menu>
            }
          />
        : null}

        {plots ?
          _.map(_.toPairs(plots), ([name, {data, layout}]) => {
            return [
              <div
                key={`label-${name}`}
                className='plots label'
              >
                {name}
              </div>,
              <div
                key={`plot-${name}`}
                className='plots plot'
                style={{
                  height: `${plotHeight || 100}px`,
                }}
              >
                <Plot
                  fluid
                  data={data}
                  layout={{
                    height: plotHeight || 100,
                    margin: {
                      t: 0,
                      l: 0,
                      r: 0,
                      b: 0,
                    },
                    xaxis: {
                      fixedrange: true,
                      autorange: false,
                      range: plotRanges.x,
                    },
                    yaxis: {
                      fixedrange: true,
                      autorange: false,
                      range: plotRanges.y,
                    },
                    showlegend: false,
                    ...layout,
                  }}
                  marker={currentCursorToPlotMarker()}
                  onHover={(e) => {
                    const point = e.points[0]
                    if (!maxValue) return
                    if (!_.isNumber(point.x)) return
                    setCursorPercent(point.x / maxValue)
                  }}
                  onPointerLeave={() => setCursorPercent(undefined)}
                  onClick={(e) => {
                    e.event.stopImmediatePropagation()
                    e.event.preventDefault()
                    const point = e.points[0]
                    if (point !== undefined) {
                      handleOnPredictionClick({
                        type: currentEvent?.type,
                        start: point.x as number,
                      })
                    }
                  }}
                />
              </div>,
            ]
          })
        : null}
      </div>
      {/* <div>
            {JSON.stringify(userLabels)}
        </div> */}
    </div>
  )
}
